if (!customElements.get('bundle-savings-pill')) {
  class BundleSavingsPill extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('.bundle-savings-pill__chip');
      this.closeBtn = this.querySelector('.bundle-savings-pill__close');
      this.panel = this.querySelector('.bundle-savings-pill__panel');
      this.savingsShortEls = this.querySelectorAll('[data-savings-short]');
      this.savingsLongEl = this.querySelector('[data-savings-long]');
      this.bundlePriceEl = this.querySelector('[data-bundle-price-display]');
      this.bundleLabelEl = this.querySelector('[data-bundle-label]');

      this.labelSubscription = this.dataset.labelBundlePriceSubscription || '';
      this.labelOneTime = this.dataset.labelBundlePrice || '';

      this.state = {
        childrenTotal: parseInt(this.dataset.childrenTotal, 10) || 0,
        bundleBasePrice: parseInt(this.dataset.bundlePrice, 10) || 0,
        sellingPlan: this.readInitialPlan(),
        planFormattedPrice: null,
      };

      this.onToggle = this.onToggle.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onPlanChange = this.onPlanChange.bind(this);

      this.productInfo = this.closest('product-info');

      this.toggleBtn.addEventListener('click', this.onToggle);
      this.closeBtn.addEventListener('click', this.onClose);

      if (this.productInfo) {
        this.productInfo.addEventListener('variant:changed', this.onVariantChange);
        this.productInfo.addEventListener('skio::update-selling-plan', this.onPlanChange);
      }

      this.catchUpToSkio();
    }

    readInitialPlan() {
      const type = this.dataset.initialPlanAdjustmentType;
      const value = this.dataset.initialPlanAdjustmentValue;
      if (!type || value === undefined) return null;
      const numeric = Number(value);
      if (isNaN(numeric)) return null;
      return { price_adjustments: [{ value_type: type, value: numeric }] };
    }

    catchUpToSkio() {
      if (!this.productInfo) return;
      const litPicker = this.productInfo.querySelector('skio-plan-picker-component');
      if (litPicker && litPicker.selectedSellingPlan) {
        this.state.sellingPlan = litPicker.selectedSellingPlan;
        this.update();
        return;
      }
      const compact = this.productInfo.querySelector('compact-skio-plan-picker');
      if (compact) {
        const select = compact.querySelector('select');
        const opt = select && select.options[select.selectedIndex];
        if (select && select.value && opt) {
          this.state.sellingPlan = { id: parseInt(select.value, 10) };
          this.state.planFormattedPrice = opt.dataset.formattedPrice || null;
          this.update();
        }
      }
    }

    disconnectedCallback() {
      if (this.productInfo) {
        this.productInfo.removeEventListener('variant:changed', this.onVariantChange);
        this.productInfo.removeEventListener('skio::update-selling-plan', this.onPlanChange);
      }
    }

    onToggle(event) {
      event.preventDefault();
      this.setOpen(!this.panel.classList.contains('is-open'));
    }

    onClose() {
      this.setOpen(false);
    }

    setOpen(open) {
      if (open) {
        this.panel.classList.add('is-open');
        this.panel.setAttribute('aria-hidden', 'false');
        this.toggleBtn.setAttribute('aria-expanded', 'true');
      } else {
        this.panel.classList.remove('is-open');
        this.panel.setAttribute('aria-hidden', 'true');
        this.toggleBtn.setAttribute('aria-expanded', 'false');
      }
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;
      this.state.bundleBasePrice = variant.price;
      this.update();
    }

    onPlanChange(event) {
      const detail = event.detail || {};
      this.state.sellingPlan = detail.sellingPlan;
      this.state.planFormattedPrice = detail.formattedPrice || null;
      if (detail.variant && typeof detail.variant.price === 'number') {
        this.state.bundleBasePrice = detail.variant.price;
      }
      this.update();
    }

    effectiveBundlePrice() {
      const base = this.state.bundleBasePrice;
      const plan = this.state.sellingPlan;
      if (!plan) return base;
      if (plan.price_adjustments && plan.price_adjustments.length) {
        const adj = plan.price_adjustments[0];
        const value = Number(adj.value);
        if (isNaN(value)) return base;
        switch (adj.value_type) {
          case 'percentage':
            return Math.round(base * (1 - value / 100));
          case 'fixed_amount':
            return Math.max(0, base - value);
          case 'price':
            return value;
          default:
            return base;
        }
      }
      const parsed = this.parseMoneyToCents(this.state.planFormattedPrice);
      if (parsed != null) return parsed;
      return base;
    }

    parseMoneyToCents(str) {
      if (!str) return null;
      const match = String(str).match(/[\d]+(?:[.,][\d]+)*/);
      if (!match) return null;
      const cleaned = match[0].replace(/,/g, '');
      const num = Number(cleaned);
      if (isNaN(num)) return null;
      return Math.round(num * 100);
    }

    formatMoney(cents) {
      if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney) {
        return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    formatMoneyShort(cents) {
      const formatted = this.formatMoney(cents);
      return formatted.replace(/[.,]00(?=\D*$)/, '');
    }

    roundSavingsUp(cents) {
      const remainder = cents % 100;
      if (remainder > 96) return cents + (100 - remainder);
      return cents;
    }

    update() {
      const effective = this.effectiveBundlePrice();
      const rawSavings = this.state.childrenTotal - effective;

      if (rawSavings <= 0) {
        this.hidden = true;
        this.setOpen(false);
        return;
      }
      this.hidden = false;

      const savings = this.roundSavingsUp(rawSavings);
      const shortSavings = this.formatMoneyShort(savings);
      this.savingsShortEls.forEach((el) => (el.textContent = shortSavings));
      if (this.savingsLongEl) this.savingsLongEl.textContent = shortSavings;

      if (this.bundlePriceEl) this.bundlePriceEl.textContent = this.formatMoney(effective);

      if (this.bundleLabelEl) {
        this.bundleLabelEl.textContent = this.state.sellingPlan
          ? this.labelSubscription
          : this.labelOneTime;
      }
    }
  }

  customElements.define('bundle-savings-pill', BundleSavingsPill);
}
